import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { NavLink } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { canAccessCoportalPage } from '@/auth/visibility';
import { IconSearch } from '@/shared/icons';
import {
  DEFAULT_HOT_PLATE_MODULE,
  LAST_MODULE_KEY,
  MY_NOTES_PATH,
  hotPlatePathForModule,
  hotPlateRequiredTile,
  isModuleSlug,
  resolveModuleFromPath,
} from './moduleNav';
import { moduleNavLabel } from '@/shared/modules/store';
import { matchMediaSupported, useMediaMatch } from './useMediaMatch';
import { pageLabel } from './pageLabel';
import { PresenceCapsule } from './presence/PresenceCapsule';
import type { PresenceRoster } from './presence/presence.api';
import type { ModuleSlug } from '@/styles/tokens';

interface Props {
  pathname: string;
  onOpenSearch: () => void;
  /** ── MOBILE ── toggles the off-canvas Dock+ModuleRail drawer (Shell owns
   *  the state). The button that calls this is hidden on desktop via CSS. */
  onToggleNav?: () => void;
  /** ── MOBILE ── current drawer state, for the hamburger's aria-expanded. */
  navOpen?: boolean;
  /**
   * Who else is in Amassa. The Shell owns the heartbeat (it survives
   * navigation; the bar only renders), so the bar never makes a request of
   * its own. Omitted or empty = no capsule.
   */
  presence?: PresenceRoster | null;
  /**
   * The notifications bell (`notifications/NotificationBell`). A slot, so
   * the Shell owns it (its poll is one shared query) and the bar stays a
   * plain renderer. Omitted = no bell.
   */
  bell?: ReactNode;
}

/**
 * Below this width the inline personal links collapse into the `⋯` overflow
 * menu. MUST stay in lock-step with `.topbar__personal { display: none }` at
 * `App.css` (max-width: 860px) — the CSS hides the inline nav, this query
 * decides which of the two affordances is rendered at all. If they drift, a
 * band of widths gets both or neither.
 *
 * Exported so `Topbar.test.tsx` can read App.css back and fail on drift —
 * the two live in different languages and nothing else would catch it.
 */
// Re-exported: Shell and the tests import it from here.
export { pageLabel };

export const PERSONAL_COMPACT_QUERY = '(max-width: 860px)';

function readLastModule(): ModuleSlug | null {
  try {
    const raw = window.sessionStorage.getItem(LAST_MODULE_KEY);
    return isModuleSlug(raw) ? raw : null;
  } catch {
    return null;
  }
}

interface PersonalLink {
  to: string;
  label: string;
  title: string;
}

/**
 * The workspace top bar — a slim context strip. Left: where you are
 * (breadcrumb). Center: the command omnibox, the OS-wide launcher (⌘K).
 * Right: who you are. Theme + sign-out live on the Dock; this bar is
 * purely context + command. The `data-mod` makes the omnibox focus ring
 * and breadcrumb accent match the active module.
 */
export function Topbar({ pathname, onOpenSearch, onToggleNav, navOpen, presence, bell }: Props) {
  const { user, canSeeTile } = useAuth();
  // Phones: one-tap Hotline WhatsApp for anyone holding either hotline grant
  // (read or reply — the write grants imply the read ones). Owner, 2026-09-24.
  const hotline =
    canSeeTile('cs-portal') &&
    (canAccessCoportalPage('hotline-whatsapp-my', false, user) ||
      canAccessCoportalPage('hotline-whatsapp-sg', false, user));
  const mod = resolveModuleFromPath(pathname);
  const onHome = pathname === '/dashboard' || pathname === '/';
  const { module, page } = pageLabel(pathname);
  // `useMediaMatch` reports false without `matchMedia`, which is safe for
  // Shell (desktop layout, nothing hidden) but NOT here: the false branch
  // renders `.topbar__personal`, and App.css hides that below 860px through
  // plain CSS that never consults matchMedia. So a narrow viewport in an
  // environment without it would show neither affordance — the exact
  // dead-end this menu was built to remove. The `⋯` menu works at every
  // width and is never hidden, so it is the honest fallback.
  const compact = useMediaMatch(PERSONAL_COMPACT_QUERY) || !matchMediaSupported();

  // Hot Plate is module-aware, and every module's board is tile-gated — the
  // HR rail entry carried `requiresTile: 'hr'`, so hoisting it without a
  // check would show every user a button that 403s.
  //
  // `/my-notes` and the hub home carry no module, so resolving Hot Plate
  // from the pathname alone returned null there and the button vanished —
  // on My Notes, the page you are most likely to jump to the board from.
  // We therefore remember the last module actually visited.
  //
  // The memory is STATE + sessionStorage, not a render-phase ref write: a
  // ref mutated during render is impure (React 19 can discard and replay a
  // render, poisoning it), and a ref does not survive the reload that a
  // bookmarked /my-notes starts from.
  const [lastMod, setLastMod] = useState<ModuleSlug | null>(readLastModule);
  useEffect(() => {
    if (!mod || mod === lastMod) return;
    setLastMod(mod);
    try {
      window.sessionStorage.setItem(LAST_MODULE_KEY, mod);
    } catch {
      /* private mode / storage disabled — in-memory state still works */
    }
  }, [mod, lastMod]);

  // Hot Plate must be reachable from EVERY page (owner directive
  // 2026-08-11). Three modules have no board of their own — `data` never
  // shipped the route, and `aiaiai`/`preferences`/`admin` are not in
  // HOT_PLATE_MODULES — so resolving from the current module alone returned
  // null and the button silently disappeared on all of their pages. We now
  // walk the candidates in order of how well they represent "the board you
  // meant" and take the first that both EXISTS and is visible to this user:
  // the current module, then the last module actually visited, then the
  // default (coportal). The tile gate is applied per candidate, not once at
  // the end, so a user who lacks `data` but holds `cs-portal` still gets a
  // working button on a /data page rather than none.
  const hotPlate = ((): { to: string; mod: ModuleSlug } | null => {
    const seen = new Set<ModuleSlug>();
    for (const cand of [mod, lastMod, DEFAULT_HOT_PLATE_MODULE]) {
      if (!cand || seen.has(cand)) continue;
      seen.add(cand);
      const to = hotPlatePathForModule(cand);
      if (to && canSeeTile(hotPlateRequiredTile(cand))) return { to, mod: cand };
    }
    return null;
  })();
  const hotPlatePath = hotPlate?.to ?? null;
  const hotPlateMod = hotPlate?.mod ?? DEFAULT_HOT_PLATE_MODULE;

  const links: PersonalLink[] = [
    {
      to: MY_NOTES_PATH,
      label: 'My Notes',
      title: 'My Notes — the same notes everywhere',
    },
  ];
  if (hotPlatePath) {
    links.push({
      to: hotPlatePath,
      label: 'Hot Plate',
      title: `Hot Plate: ${moduleNavLabel(hotPlateMod)} team board`,
    });
  }

  return (
    <header className="topbar" data-mod={mod ?? undefined}>
      {/* ── MOBILE ── hamburger: opens the off-canvas nav drawer. Hidden on
          desktop (>768px) via `.topbar__hamburger { display:none }`. */}
      {onToggleNav && (
        <button
          type="button"
          className="topbar__hamburger"
          onClick={onToggleNav}
          aria-label="Menu"
          aria-expanded={!!navOpen}
          aria-controls="shell-nav"
        >
          <span aria-hidden className="topbar__hamburger-glyph">
            {navOpen ? (
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            )}
          </span>
        </button>
      )}

      <div className="topbar__crumb">
        {onHome ? (
          <span className="topbar__here">Hub</span>
        ) : (
          <>
            <span className="topbar__crumb-mod">{module ?? 'Amassa'}</span>
            {page && (
              <>
                <span className="topbar__crumb-sep" aria-hidden>›</span>
                <span className="topbar__here">{page}</span>
              </>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        className="topbar__omni"
        data-tour="search"
        onClick={onOpenSearch}
        aria-label="Open the command bar"
      >
        <IconSearch size={15} />
        <span className="topbar__omni-text">Search refs, guests, files, suppliers, pages…</span>
        <span className="topbar__omni-kbd">⌘K</span>
      </button>

      {/* ── Personal surfaces (2026-07-29, made responsive 2026-07-31) ──
          My Notes and Hot Plate used to sit in every module's rail — 13 and
          12 duplicate entries. They live here instead, but they are NOT the
          same kind of thing:

            My Notes  → one global route. Every module's page was a thin
                        wrapper around the same /api/notes, so a single
                        destination loses nothing.
            Hot Plate → the CURRENT module's team board. Each module's
                        board is scoped `mod=<slug> scope=team`; one
                        global route would merge every team's cards, which
                        is the bug the 2026-05-25 mod-scope migration
                        closes. Hidden on pages with no board (/dashboard
                        before any module is visited, aiaiai, admin, data)
                        rather than linking somewhere arbitrary.

          Below 860px the two inline links would push the user block off the
          bar, so they collapse into a `⋯` overflow menu instead of being
          hidden outright — hiding them left phones with no entry point to
          either surface anywhere in the app, since the rail entries are
          gone for good. */}
      {compact && hotline && (
        <NavLink
          to="/coportal/hotline-whatsapp"
          className={({ isActive }) => `topbar__hotline${isActive ? ' is-active' : ''}`}
          aria-label="WhatsApp"
          title="WhatsApp"
        >
          <HotlineGlyph />
        </NavLink>
      )}
      {compact ? (
        <PersonalMenu
          links={links}
          active={links.some((l) => l.to === pathname)}
        />
      ) : (
        <nav className="topbar__personal" aria-label="Personal">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `topbar__personal-link${isActive ? ' is-active' : ''}`
              }
              title={l.title}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}

      {bell}

      <PresenceCapsule roster={presence ?? null} compact={compact} />

      <div className="topbar__user">
        {user && (
          <>
            <span className="topbar__user-name">{user.fullName || user.email}</span>
            <span className="topbar__user-role">{user.role}</span>
          </>
        )}
      </div>
    </header>
  );
}

/**
 * Narrow-screen affordance for the personal surfaces — a `⋯` button that
 * drops a small opaque menu under the top bar.
 *
 * Deliberately not a portal and not a `<dialog>`: it is two rows, anchored
 * to its trigger, and closing on outside-click / Esc / route change is the
 * whole interaction model. The panel uses `--surface-2` per the overlay
 * rule (never raw `--bg-card` as a floating surface) and takes the woven
 * signal edge on top so it reads as Amassa chrome rather than a browser
 * context menu.
 */
function PersonalMenu({
  links,
  active,
}: {
  links: PersonalLink[];
  /** One of the personal surfaces IS the current page — the collapsed
   *  trigger has to say so, or the "you are here" signal disappears at
   *  exactly the width where the labels did. */
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  }, []);

  // Outside click + Esc. Bound while open only.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Move focus into the menu when it opens — it is a keyboard surface, not
  // just a tap target.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const here = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'ArrowDown'
        ? (here + 1 + items.length) % items.length
        : (here - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div ref={wrapRef} style={menuWrapStyle}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="My Notes and Hot Plate"
        title="My Notes and Hot Plate"
        style={{
          ...menuTriggerStyle,
          color: active
            ? 'var(--accent, var(--brand))'
            : open
              ? 'var(--text)'
              : 'var(--text-muted)',
          borderColor: active
            ? 'color-mix(in oklab, var(--accent, var(--brand)) 30%, transparent)'
            : open
              ? 'var(--border-strong)'
              : 'transparent',
          background:
            open || active
              ? 'color-mix(in oklab, var(--accent, var(--brand)) 10%, transparent)'
              : 'transparent',
        }}
      >
        <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden fill="currentColor">
          <circle cx="12" cy="5" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="12" cy="19" r="1.7" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Personal"
          className="motion-scale hub-overlay"
          onKeyDown={onMenuKeyDown}
          style={menuPanelStyle}
        >
          <span aria-hidden style={menuEdgeStyle} />
          <span style={menuKickerStyle}>Personal</span>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              role="menuitem"
              title={l.title}
              onClick={() => close(false)}
              className={({ isActive }) =>
                `topbar__personal-link${isActive ? ' is-active' : ''}`
              }
              style={menuItemStyle}
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Styles (inline: App.css is token/chrome-only and owns no menu) ──────

const menuWrapStyle: CSSProperties = {
  position: 'relative',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

const menuTriggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  padding: 0,
  borderRadius: 'var(--q-r-md, 8px)',
  border: '1px solid transparent',
  cursor: 'pointer',
  transition:
    'color 140ms var(--ease-weave), border-color 140ms var(--ease-weave), background 140ms var(--ease-weave)',
};

const menuPanelStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 9px)',
  insetInlineEnd: 0,
  zIndex: 40,
  minWidth: 186,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 8px 9px',
  // Opaque overlay surface — never raw --bg-card as a floating panel.
  background: 'var(--surface-2)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--hm-radius-md)',
  boxShadow: 'var(--q-shadow-3, 0 18px 38px -18px rgba(0,0,0,0.55))',
  overflow: 'hidden',
  transformOrigin: 'top right',
};

/** The woven signal edge — the same 2px orange→cyan top rule modals carry. */
const menuEdgeStyle: CSSProperties = {
  position: 'absolute',
  insetBlockStart: 0,
  insetInline: 0,
  height: 2,
  background: 'var(--grad-signal)',
  pointerEvents: 'none',
};

const menuKickerStyle: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 9,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--text-dim)',
  padding: '4px 8px 2px',
};

const menuItemStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  padding: '9px 10px',
};

/** A speech bubble with a handset notch — hand-drawn, not an icon set. */
function HotlineGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 2.5a7.5 7.5 0 0 0-6.5 11.2L2.6 17.4l3.8-.9A7.5 7.5 0 1 0 10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M7.6 7.4c.2-.4.6-.5.9-.3l.8.9c.2.3.2.6 0 .9l-.4.4c.4.9 1.1 1.6 2 2l.4-.4c.3-.2.6-.2.9 0l.9.8c.2.3.1.7-.3.9-.7.5-1.6.6-2.4.2a6.2 6.2 0 0 1-3-3c-.4-.8-.3-1.7.2-2.4Z"
        fill="currentColor"
      />
    </svg>
  );
}
